import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axiosInstance, { AUTH_EXPIRED_EVENT, clearAuthStorage } from '../api/axiosInstance';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // 권한 플래그는 localStorage에서 초기화하지 않음 — /api/users/me가 단일 출처
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  const [role, setRole] = useState(() => localStorage.getItem('primaryRole'));
  const [isTalent, setIsTalent] = useState(false);
  const [isProducer, setIsProducer] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isQnaAdmin, setIsQnaAdmin] = useState(false);
  const [isTest, setIsTest] = useState(false);

  // 표시용 정보는 localStorage 캐시 허용 (토큰·권한과 무관)
  const [profileId, setProfileId] = useState(() => localStorage.getItem('profileId'));
  // 대행(MANAGED) 계정 여부와 최초 비밀번호 변경 필요 여부 — /api/users/me가 단일 출처
  const [accountType, setAccountType] = useState('NORMAL');
  const [mustChangePassword, setMustChangePassword] = useState(false);
  // 위 두 값을 서버에서 읽었는지. 로그인 직후 읽기 전에 화면을 그리면
  // 대행 계정이 일반 계정 화면으로 한 번 보였다가 새로고침해야 바뀐다.
  const [accountLoaded, setAccountLoaded] = useState(false);
  const [userId, setUserId] = useState(() => { const v = localStorage.getItem('userId'); return v ? Number(v) : null; });
  const [userName, setUserName] = useState(() => localStorage.getItem('userName') || null);
  const [userStageName, setUserStageName] = useState(() => localStorage.getItem('userStageName') || null);
  const [userRealName, setUserRealName] = useState(() => localStorage.getItem('userRealName') || null);
  const [userProfileImage, setUserProfileImage] = useState(() => {
    const v = localStorage.getItem('userProfileImage');
    return v && v !== 'null' ? v : null;
  });

  // 앱 시작 시 쿠키 세션 검증. 계정 승격처럼 서버 상태가 바뀐 뒤에도 다시 호출한다.
  const refreshSession = useCallback(async () => {
    try {
        const res = await axiosInstance.get('/api/users/me');
        const d = res.data;
        const talentFlag = d.isTalent ?? (d.primaryRole === 'TALENT');
        const producerFlag = d.isProducer ?? (d.primaryRole === 'PRODUCER');
        const adminFlag = d.isAdmin ?? false;
        const qnaAdminFlag = d.isQnaAdmin ?? false;
        const uid = d.id ?? d.userId ?? d.memberId ?? null;

        setIsTalent(talentFlag);
        setIsProducer(producerFlag);
        setIsAdmin(adminFlag);
        setIsQnaAdmin(qnaAdminFlag);
        setIsTest(d.isTest ?? false);
        if (uid != null) { setUserId(Number(uid)); localStorage.setItem('userId', String(uid)); }

        // 배우 프로필 ID는 세션 응답이 단일 출처 — localStorage만 믿으면
        // 새 기기/브라우저로 로그인한 기존 배우가 profileId 없이 시작한다
        setAccountType(d.accountType === 'MANAGED' ? 'MANAGED' : 'NORMAL');
        setMustChangePassword(Boolean(d.mustChangePassword));

        const tpid = d.talentProfileId ?? null;
        if (tpid != null) {
          setProfileId(String(tpid));
          localStorage.setItem('profileId', String(tpid));
        }

        const activeRole = d.primaryRole || localStorage.getItem('primaryRole')
          || (talentFlag ? 'TALENT' : producerFlag ? 'PRODUCER' : null);
        if (activeRole) { setRole(activeRole); localStorage.setItem('primaryRole', activeRole); }

      setIsLoggedIn(true);
    } catch {
      // 401 또는 네트워크 오류 → 미로그인 상태, 리다이렉트 없음
      setIsLoggedIn(false);
    } finally {
      setAccountLoaded(true);
      setAuthReady(true);
    }
  }, []);

  useEffect(() => { refreshSession(); }, [refreshSession]);

  /**
   * 대행 계정 여부와 최초 비밀번호 변경 필요 여부만 읽는다.
   *
   * 로그인 응답에는 이 두 값이 없어, 로그인 직후에 따로 읽어야 한다.
   * refreshSession을 쓰지 않는 이유 —
   *   · 역할을 서버 값으로 덮어써, 테스트 계정의 두 역할이나 방금 고른 역할이 바뀐다
   *   · 실패하면 로그아웃 처리해, 한 번의 네트워크 오류로 방금 로그인한 사람이 튕긴다
   */
  const loadAccountState = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/api/users/me');
      setAccountType(data?.accountType === 'MANAGED' ? 'MANAGED' : 'NORMAL');
      setMustChangePassword(Boolean(data?.mustChangePassword));
    } catch {
      // 못 읽어도 로그인 상태는 건드리지 않는다. 다음 새로고침 때 다시 읽힌다.
    } finally {
      setAccountLoaded(true);
    }
  }, []);

  const login = useCallback(({ primaryRole, isTalent: it, isProducer: ip, isAdmin: ia, isQnaAdmin: iqa, isTest: ist, userId: uid }) => {
    const talentFlag = it ?? (primaryRole === 'TALENT');
    const producerFlag = ip ?? (primaryRole === 'PRODUCER');
    const adminFlag = ia ?? false;
    const qnaAdminFlag = iqa ?? false;

    setIsTalent(talentFlag);
    setIsProducer(producerFlag);
    setIsAdmin(adminFlag);
    setIsQnaAdmin(qnaAdminFlag);
    setIsTest(ist ?? false);
    if (uid != null) { setUserId(Number(uid)); localStorage.setItem('userId', String(uid)); }

    const activeRole = primaryRole || localStorage.getItem('primaryRole')
      || (talentFlag ? 'TALENT' : producerFlag ? 'PRODUCER' : null);
    if (activeRole) { setRole(activeRole); localStorage.setItem('primaryRole', activeRole); }

    setIsLoggedIn(true);

    // 대행 계정 여부를 읽는 동안 보호된 화면은 기다린다 (PrivateRoute)
    setAccountLoaded(false);
    return loadAccountState();
  }, [loadAccountState]);

  const setProfile = useCallback((id) => {
    localStorage.setItem('profileId', String(id));
    setProfileId(String(id));
  }, []);

  const setUserInfo = useCallback(({ name, profileImage, stageName, realName }) => {
    if (name != null) { localStorage.setItem('userName', name); setUserName(name); }
    if (stageName !== undefined) {
      if (stageName) { localStorage.setItem('userStageName', stageName); setUserStageName(stageName); }
      else { localStorage.removeItem('userStageName'); setUserStageName(null); }
    }
    if (realName !== undefined) {
      if (realName) { localStorage.setItem('userRealName', realName); setUserRealName(realName); }
      else { localStorage.removeItem('userRealName'); setUserRealName(null); }
    }
    localStorage.setItem('userProfileImage', profileImage || '');
    setUserProfileImage(profileImage);
  }, []);

  const switchRole = useCallback((newRole) => {
    localStorage.setItem('primaryRole', newRole);
    setRole(newRole);
    localStorage.removeItem('userName');
    localStorage.removeItem('userStageName');
    localStorage.removeItem('userRealName');
    localStorage.setItem('userProfileImage', '');
    setUserName(null);
    setUserStageName(null);
    setUserRealName(null);
    setUserProfileImage(null);
    if (newRole === 'TALENT' && !isTalent) { setIsTalent(true); }
    if (newRole === 'PRODUCER' && !isProducer) { setIsProducer(true); }
  }, [isTalent, isProducer]);

  /**
   * 로그인 흔적을 지운다. 로그아웃과 세션 만료가 함께 쓴다.
   * 서버 쿠키는 httpOnly라 여기서 지울 수 없다 — 로그아웃은 반드시 서버를 먼저 부른다.
   */
  const resetSession = useCallback(() => {
    clearAuthStorage();
    setIsLoggedIn(false);
    setRole(null);
    setIsTalent(false);
    setIsProducer(false);
    setIsAdmin(false);
    setIsQnaAdmin(false);
    setIsTest(false);
    setProfileId(null);
    setAccountType('NORMAL');
    setMustChangePassword(false);
    setAccountLoaded(true);
    setUserName(null);
    setUserStageName(null);
    setUserRealName(null);
    setUserProfileImage(null);
    setUserId(null);
  }, []);

  const logout = useCallback(async () => {
    // 서버가 토큰을 폐기하고 쿠키를 지운다 — 이미 만료됐어도 항상 200이다.
    // 프론트 상태만 비우면 쿠키가 남아 로그인이 유지되므로 반드시 먼저 부른다.
    try {
      await axiosInstance.post('/api/auth/logout');
    } catch {}
    resetSession();
  }, [resetSession]);

  // 갱신까지 실패해 세션이 끝났다는 알림. 로그인해 있던 경우에만 비운다 —
  // 비로그인 방문자도 앱을 켤 때 이 알림을 받으므로, 매번 지우면 저장소가 계속 비워진다.
  const isLoggedInRef = useRef(isLoggedIn);
  isLoggedInRef.current = isLoggedIn;
  useEffect(() => {
    const onExpired = () => { if (isLoggedInRef.current) resetSession(); };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, [resetSession]);

  if (!authReady) return null;

  return (
    <AuthContext.Provider value={{ isLoggedIn, role, isTalent, isProducer, isAdmin, isQnaAdmin, isTest, profileId, accountType, isManagedAccount: accountType === 'MANAGED', accountLoaded, mustChangePassword, setMustChangePassword, refreshSession, userId, userName, userStageName, userRealName, userProfileImage, login, logout, setProfile, setUserInfo, switchRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
